import { ValidateSqlInjection } from './Security';
import * as XLSX from 'xlsx';
import { GetSessionData } from './Sessions';
import {
    GetTriviaQuestions,
    SubmitTrivia,
    CheckDuplicateParticipant,
    LogScreenshot,
    GetExportDataset,
    TrackMetric,
    GetPublicConfig,
    RecoverPass,
    GetAdminConfig,
    UpdateAdminConfig,
    GetAdminQuestions,
    SaveAdminQuestion,
    DeleteAdminQuestion,
    GetAdminParticipants,
    GetAdminStats,
    DisqualifyParticipant as DLDisqualifyParticipant,
    UndisqualifyParticipant as DLUndisqualifyParticipant,
    ResetDynamic,
    GetWinnerCount,
    CheckinParticipant as DLCheckinParticipant,
    SetOverlayVisibilityDL,
    SetOverlayOffsetDL,
    UploadMedia as DLUploadMedia
} from '../DL/QuizzB9Zone';

const STORAGE_KEY = 'quizzb9zone_my_passes';

/**
 * Obtiene el nombre del operador administrador autenticado en la sesión, con fallback a "Team Interaktia".
 */
const GetAdminOperatorName = async () => {
    try {
        const sessionRes = await GetSessionData(true);
        if (sessionRes && sessionRes.success && sessionRes.data) {
            const user = sessionRes.data;
            const op = user.name || user.fullName || user.fullname || user.email || user.username || user.user;
            if (op && typeof op === 'string' && op.trim().length > 0) {
                return op.trim();
            }
        }
    } catch (e) {
        console.warn("No se pudo obtener el nombre de operador de la sesión:", e);
    }
    return "Team Interaktia";
};

/**
 * Formatea una fecha UTC al huso horario de México.
 */
const formatMexicoDate = (dateString) => {
    if (!dateString) return 'No disponible';
    const date = new Date(dateString);
    return date.toLocaleString('es-MX', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false
    }) + ' hrs';
};

/**
 * Descalifica a un participante registrando automáticamente el operador.
 */
const DisqualifyParticipant = async (participantId, reason, operator = null) => {
    const finalOperator = operator || await GetAdminOperatorName();
    return await DLDisqualifyParticipant(participantId, reason, finalOperator);
};

/**
 * Remueve la descalificación de un participante registrando la restitución y el operador.
 */
const UndisqualifyParticipant = async (participantId, operator = null) => {
    const finalOperator = operator || await GetAdminOperatorName();
    return await DLUndisqualifyParticipant(participantId, finalOperator);
};

/**
 * Valida un pase escaneado en el acceso físico registrando el operador.
 */
const CheckinParticipant = async (scanData, operator = null) => {
    const finalOperator = operator || await GetAdminOperatorName();
    return await DLCheckinParticipant(scanData, finalOperator);
};

/**
 * Genera y descarga el archivo Excel (.xlsx) estructurado en 4 hojas independientes con columnas discretas.
 */
const ExportParticipantsToExcel = (datasetOrParticipants = [], questionsParam = [], matchTitleParam = 'Convivencia en la zona', recoveriesParam = [], interactionsParam = []) => {
    let participants = [];
    let questions = [];
    let matchTitle = 'Convivencia en la zona';
    let recoveryLogs = [];
    let screenshotLogs = [];

    if (datasetOrParticipants && !Array.isArray(datasetOrParticipants) && typeof datasetOrParticipants === 'object') {
        // Formato Dataset Objeto
        participants = datasetOrParticipants.participants || [];
        questions = datasetOrParticipants.questions || questionsParam || [];
        matchTitle = datasetOrParticipants.matchTitle || matchTitleParam || 'Convivencia en la zona';
        recoveryLogs = datasetOrParticipants.recoveryLogs || [];
        screenshotLogs = datasetOrParticipants.screenshotLogs || [];
    } else {
        // Formato Arrays
        participants = Array.isArray(datasetOrParticipants) ? datasetOrParticipants : [];
        questions = Array.isArray(questionsParam) ? questionsParam : [];
        matchTitle = matchTitleParam || 'Convivencia en la zona';
        recoveryLogs = Array.isArray(recoveriesParam) ? recoveriesParam : [];
        screenshotLogs = Array.isArray(interactionsParam) ? interactionsParam : [];
    }

    if (!participants || participants.length === 0) return false;

    // Ordenar participantes cronológicamente por fecha de registro
    const sortedParticipants = [...participants].sort((a, b) => new Date(a.createdAtUtc) - new Date(b.createdAtUtc));

    // ====================================================
    // HOJA 1: Padrón de Asistencia y Pases (Control General)
    // ====================================================
    const sheet1Data = sortedParticipants.map((p) => {
        return {
            'ID': p.id,
            'Fecha Registro': formatMexicoDate(p.createdAtUtc),
            'Nombre Aficionado': p.name,
            'Correo': p.email,
            'Teléfono': p.phone,
            'Folio': p.passCode || 'N/A',
            'Ranking Ganador': p.isWinner ? `#${p.winnerRank}` : 'No ganador',
            '¿Es Ganador?': p.isWinner ? 'SÍ' : 'NO',
            '¿Ingresó a Cancha?': p.hasAttended ? 'SÍ' : 'NO',
            'Fecha y Hora Ingreso': p.hasAttended && p.attendedAtUtc ? formatMexicoDate(p.attendedAtUtc) : 'N/A',
            'Validado en Cancha Por': p.hasAttended ? (p.checkedInBy || 'Team Interaktia') : 'N/A',
            '¿Descalificado?': p.isDisqualified ? 'SÍ' : 'NO',
            'Fecha y Hora Descalificación': p.isDisqualified && p.disqualifiedAtUtc ? formatMexicoDate(p.disqualifiedAtUtc) : 'N/A',
            'Descalificado Por': p.isDisqualified ? (p.disqualifiedBy || 'Team Interaktia') : 'N/A',
            'Motivo Descalificación': p.isDisqualified ? (p.disqualifiedReason || 'N/A') : 'N/A',
            '¿Fue Restituido?': p.wasRestituted ? 'SÍ' : 'NO',
            'Fecha y Hora Restitución': p.wasRestituted && p.restitutedAtUtc ? formatMexicoDate(p.restitutedAtUtc) : 'N/A',
            'Restituido Por': p.wasRestituted ? (p.restitutedBy || 'Team Interaktia') : 'N/A',
            'Puntaje Obtenido': p.score,
            'Total Preguntas': p.totalQuestions || questions.length,
            '¿Captura Guardada?': p.hasSavedScreenshot ? 'SÍ' : 'NO',
            'Cantidad de Capturas': p.screenshotCount || 0,
            'Token Verificación': p.verificationToken || 'N/A'
        };
    });

    // ====================================================
    // HOJA 2: Tiempos y Respuestas de Trivia (Análisis Detallado)
    // ====================================================
    const sheet2Data = sortedParticipants.map((p) => {
        let chosenOptionMap = {};
        try {
            if (p.selectedOptionIdsJson) {
                const parsed = typeof p.selectedOptionIdsJson === 'string' ? JSON.parse(p.selectedOptionIdsJson) : p.selectedOptionIdsJson;
                if (Array.isArray(parsed)) {
                    parsed.forEach(ans => {
                        if (ans && ans.QuestionId !== undefined) {
                            chosenOptionMap[ans.QuestionId] = ans.OptionId;
                        } else if (ans && ans.questionId !== undefined) {
                            chosenOptionMap[ans.questionId] = ans.optionId;
                        }
                    });
                }
            }
        } catch (err) {
            console.error("Error al parsear respuestas de participante", p.id, err);
        }

        let questionTimesMap = {};
        let questionTimesList = [];
        try {
            if (p.questionTimesJson) {
                const parsedTimes = typeof p.questionTimesJson === 'string' ? JSON.parse(p.questionTimesJson) : p.questionTimesJson;
                if (Array.isArray(parsedTimes)) {
                    questionTimesList = parsedTimes;
                    parsedTimes.forEach((t, idx) => {
                        const qId = t.questionId !== undefined ? t.questionId : t.QuestionId !== undefined ? t.QuestionId : (questions[idx]?.id || idx + 1);
                        const dur = t.durationSeconds !== undefined ? t.durationSeconds : t.DurationSeconds !== undefined ? t.DurationSeconds : null;
                        questionTimesMap[qId] = dur;
                    });
                }
            }
        } catch (err) {
            console.error("Error al parsear tiempos de preguntas de participante", p.id, err);
        }

        // Calcular promedio de tiempo por pregunta
        let avgQuestionTime = 'N/A';
        if (questionTimesList.length > 0) {
            const sum = questionTimesList.reduce((acc, curr) => acc + (curr.durationSeconds || curr.DurationSeconds || 0), 0);
            avgQuestionTime = (sum / questionTimesList.length).toFixed(2);
        } else if (p.triviaDurationSeconds && questions.length > 0) {
            avgQuestionTime = (p.triviaDurationSeconds / questions.length).toFixed(2);
        }

        const row = {
            'ID Participante': p.id,
            'Nombre Aficionado': p.name,
            'Folio': p.passCode || 'N/A',
            '¿Es Ganador?': p.isWinner ? 'SÍ' : 'NO',
            'Tiempo Total Sesión (s)': p.totalSessionTimeSeconds != null ? p.totalSessionTimeSeconds : 'N/A',
            'Tiempo Trivia (s)': p.triviaDurationSeconds != null ? p.triviaDurationSeconds : 'N/A',
            'Tiempo Promedio Pregunta (s)': avgQuestionTime
        };

        // Columnas dinámicas por cada pregunta: tiempos individuales y respuestas
        questions.forEach((q, qIdx) => {
            const qNum = qIdx + 1;
            const timeKey = `Tiempo P${qNum} (s)`;
            const qTime = questionTimesMap[q.id] ?? (questionTimesList[qIdx]?.durationSeconds || questionTimesList[qIdx]?.DurationSeconds);
            row[timeKey] = qTime != null ? qTime : 'N/A';

            const questionKey = `Respuesta P${qNum}: ${q.instruction}`;
            const selectedOptionId = chosenOptionMap[q.id];
            const chosenOption = (q.options || []).find(opt => opt.id === selectedOptionId);
            row[questionKey] = chosenOption ? chosenOption.text : 'No contestada';
        });

        row['Puntaje Total'] = `${p.score} de ${p.totalQuestions || questions.length}`;
        return row;
    });

    // ====================================================
    // HOJA 3: Historial de Recuperaciones
    // ====================================================
    const sheet3Data = (recoveryLogs && recoveryLogs.length > 0)
        ? recoveryLogs.map((l) => ({
            'ID Registro': l.id,
            'Fecha y Hora Consulta': formatMexicoDate(l.createdAtUtc),
            'ID Participante': l.participantId || 'N/A',
            'Nombre Aficionado': l.name || 'N/A',
            'Folio Recuperado': l.passCode || 'N/A',
            'Dato Buscado': l.queryUsed || 'N/A',
            'Tipo Búsqueda': l.searchType || 'N/A',
            '¿Recuperación Exitosa?': l.wasSuccessful ? 'SÍ' : 'NO',
            'Dirección IP': l.clientIp || 'N/A',
            'Navegador / Dispositivo': l.userAgent || 'N/A'
        }))
        : [{ 'Estado': 'Sin registros de consulta o recuperación aún' }];

    // ====================================================
    // HOJA 4: Interacciones y Capturas
    // ====================================================
    const sheet4Data = (screenshotLogs && screenshotLogs.length > 0)
        ? screenshotLogs.map((s) => {
            let methodLabel = s.method || 'N/A';
            if (s.method === 'BUTTON_DOWNLOAD') methodLabel = 'Botón Guardar en Galería';
            else if (s.method === 'KEYBOARD_SHORTCUT') methodLabel = 'Atajo de Teclado (PrintScreen)';
            else if (s.method === 'HEURISTIC') methodLabel = 'Detección de Cambio de Pestaña / Visibilidad';

            return {
                'ID Registro': s.id,
                'Fecha y Hora Captura': formatMexicoDate(s.createdAtUtc),
                'ID Participante': s.participantId || 'N/A',
                'Nombre Aficionado': s.name || 'N/A',
                'Folio': s.passCode || 'N/A',
                'Método de Guardado': methodLabel,
                'Dirección IP': s.clientIp || 'N/A'
            };
        })
        : [{ 'Estado': 'Sin registros de capturas de pantalla aún' }];

    // Crear libro con las 4 pestañas
    const workbook = XLSX.utils.book_new();
    const worksheet1 = XLSX.utils.json_to_sheet(sheet1Data);
    const worksheet2 = XLSX.utils.json_to_sheet(sheet2Data);
    const worksheet3 = XLSX.utils.json_to_sheet(sheet3Data);
    const worksheet4 = XLSX.utils.json_to_sheet(sheet4Data);

    XLSX.utils.book_append_sheet(workbook, worksheet1, 'Padrón de Asistencia y Pases');
    XLSX.utils.book_append_sheet(workbook, worksheet2, 'Tiempos y Respuestas Trivia');
    XLSX.utils.book_append_sheet(workbook, worksheet3, 'Historial de Recuperaciones');
    XLSX.utils.book_append_sheet(workbook, worksheet4, 'Interacciones y Capturas');

    const cleanTitle = matchTitle.replace(/[^a-zA-Z0-9_-]/g, '_');
    const dateStr = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(workbook, `Reporte_Convivencia_Zona_${cleanTitle}_${dateStr}.xlsx`);

    return true;
};

/**
 * Obtiene todos los pases dorados guardados localmente en el dispositivo.
 * @returns {Array} Lista de pases ganados.
 */
const GetStoredPasses = () => {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (!stored) return [];
        const parsed = JSON.parse(stored);
        return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
        console.error("Error al leer pases de localStorage:", error);
        return [];
    }
};

/**
 * Guarda o actualiza un pase ganado en el almacenamiento local multi-pase.
 * @param {Object} passData Datos del pase ganado.
 */
const SavePassToStorage = (passData) => {
    try {
        if (!passData || !passData.passCode) return;
        const current = GetStoredPasses();
        
        // Evitar duplicados por passCode
        const existingIndex = current.findIndex(p => p.passCode === passData.passCode);
        if (existingIndex >= 0) {
            current[existingIndex] = { ...current[existingIndex], ...passData, updatedAt: new Date().toISOString() };
        } else {
            current.unshift({ ...passData, savedAt: new Date().toISOString() });
        }
        
        localStorage.setItem(STORAGE_KEY, JSON.stringify(current));
    } catch (error) {
        console.error("Error al guardar pase en localStorage:", error);
    }
};

/**
 * Valida los campos del formulario de registro móvil del aficionado y previene inyecciones SQL.
 * @param {Object} formData { name, email, phone, acceptedTerms }
 * @returns {Promise<{ isValid: boolean, error?: string }>}
 */
const ValidateFanForm = async (formData) => {
    if (!formData) {
        return { isValid: false, error: "Por favor completa el formulario." };
    }

    const { name, email, phone, acceptedTerms } = formData;

    if (!name || name.trim().length < 3) {
        return { isValid: false, error: "Por favor ingresa tu nombre completo (mínimo 3 caracteres)." };
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email || !emailRegex.test(email.trim())) {
        return { isValid: false, error: "Por favor ingresa un correo electrónico válido." };
    }

    const cleanPhone = phone ? phone.replace(/\D/g, '') : '';
    if (cleanPhone.length !== 10) {
        return { isValid: false, error: "Por favor ingresa tu número telefónico a 10 dígitos." };
    }

    if (!acceptedTerms) {
        return { isValid: false, error: "Debes aceptar los términos y condiciones para continuar." };
    }

    // Validación contra inyecciones SQL
    const isNameDangerous = await ValidateSqlInjection(name);
    const isEmailDangerous = await ValidateSqlInjection(email);
    const isPhoneDangerous = await ValidateSqlInjection(phone);

    if (isNameDangerous || isEmailDangerous || isPhoneDangerous) {
        return { isValid: false, error: "Se detectaron caracteres no permitidos en el formulario." };
    }

    return { isValid: true };
};

/**
 * Enmascara un número de teléfono para visualización sutil en la tarjeta (ej. ******1234).
 */
const MaskPhone = (phone) => {
    if (!phone) return '';
    const clean = phone.replace(/\D/g, '');
    if (clean.length < 4) return clean;
    return `******${clean.slice(-4)}`;
};

/**
 * Enmascara un correo electrónico para visualización sutil en la tarjeta (ej. j***z@dominio.com).
 */
const MaskEmail = (email) => {
    if (!email || !email.includes('@')) return email || '';
    const [user, domain] = email.split('@');
    if (user.length <= 2) return `${user[0]}*@${domain}`;
    return `${user[0]}***${user[user.length - 1]}@${domain}`;
};

/**
 * Sintetizador Web Audio API para feedback sonoro en tiempo real (0ms de latencia).
 * @param {'success' | 'warning' | 'error'} type Tipo de sonido.
 */
const PlayCheckinSound = (type = 'success') => {
    try {
        const AudioContext = window.AudioContext || window.webkitAudioContext;
        if (!AudioContext) return;
        const ctx = new AudioContext();

        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);

        const now = ctx.currentTime;

        if (type === 'success') {
            // Tono limpio y brillante ascendente (D5 -> A5)
            osc.type = 'sine';
            osc.frequency.setValueAtTime(587.33, now); // D5
            osc.frequency.exponentialRampToValueAtTime(880.00, now + 0.15); // A5
            gain.gain.setValueAtTime(0.3, now);
            gain.gain.exponentialRampToValueAtTime(0.01, now + 0.35);
            osc.start(now);
            osc.stop(now + 0.35);
        } else if (type === 'warning') {
            // Doble tono ámbar de advertencia (440Hz -> 440Hz)
            osc.type = 'triangle';
            osc.frequency.setValueAtTime(440, now);
            gain.gain.setValueAtTime(0.3, now);
            gain.gain.exponentialRampToValueAtTime(0.01, now + 0.12);
            osc.start(now);
            osc.stop(now + 0.12);

            const osc2 = ctx.createOscillator();
            const gain2 = ctx.createGain();
            osc2.connect(gain2);
            gain2.connect(ctx.destination);
            osc2.type = 'triangle';
            osc2.frequency.setValueAtTime(440, now + 0.16);
            gain2.gain.setValueAtTime(0.3, now + 0.16);
            gain2.gain.exponentialRampToValueAtTime(0.01, now + 0.32);
            osc2.start(now + 0.16);
            osc2.stop(now + 0.32);
        } else {
            // Tono grave de error / rechazo (220Hz -> 130Hz)
            osc.type = 'sawtooth';
            osc.frequency.setValueAtTime(220, now);
            osc.frequency.exponentialRampToValueAtTime(130, now + 0.3);
            gain.gain.setValueAtTime(0.25, now);
            gain.gain.exponentialRampToValueAtTime(0.01, now + 0.35);
            osc.start(now);
            osc.stop(now + 0.35);
        }
    } catch (e) {
        console.warn("Audio playback no soportado o bloqueado por navegador:", e);
    }
};

/**
 * Actualiza la visibilidad del overlay en tiempo real (OBS / Pantallas).
 * @param {boolean} isVisible
 */
const SetOverlayVisibility = async (isVisible) => {
    try {
        const response = await SetOverlayVisibilityDL(isVisible);
        return response;
    } catch (error) {
        console.error("Error en SetOverlayVisibility:", error);
        return { success: false, message: 'Error al actualizar visibilidad del overlay.' };
    }
};

/**
 * Actualiza la posición vertical (Offset Y) del overlay en tiempo real (OBS / Pantallas).
 * @param {number} offsetY
 */
const SetOverlayOffset = async (offsetY) => {
    try {
        const response = await SetOverlayOffsetDL(offsetY);
        return response;
    } catch (error) {
        console.error("Error en SetOverlayOffset:", error);
        return { success: false, message: 'Error al actualizar posición del overlay.' };
    }
};

/**
 * Sube y procesa un archivo multimedia para QuizzB9Zone.
 * @param {File} fileObject
 * @param {string} mediaType - "banner_md", "banner_lg", "logo"
 */
const UploadMedia = async (fileObject, mediaType = '') => {
    if (!fileObject) {
        return { success: false, message: 'No se ha seleccionado ningún archivo.' };
    }
    const formData = new FormData();
    formData.append('file', fileObject);
    if (mediaType) {
        formData.append('type', mediaType);
    }
    return await DLUploadMedia(formData);
};

/**
 * Resuelve una URL de archivo multimedia relativa al servidor de la API o absoluta.
 * @param {string} mediaPath
 * @returns {string}
 */
const resolveMediaUrl = (mediaPath) => {
    if (!mediaPath) return '';
    if (
        mediaPath.startsWith('http://') ||
        mediaPath.startsWith('https://') ||
        mediaPath.startsWith('blob:') ||
        mediaPath.startsWith('data:')
    ) {
        return mediaPath;
    }
    const rawApiUrl = process.env.REACT_APP_API_URL;
    if (!rawApiUrl) {
        return mediaPath;
    }
    const apiBaseUrl = rawApiUrl.replace(/\/api\/?$/, '/');
    const cleanBase = apiBaseUrl.endsWith('/') ? apiBaseUrl : `${apiBaseUrl}/`;
    const cleanPath = mediaPath.startsWith('/') ? mediaPath.substring(1) : mediaPath;
    return `${cleanBase}${cleanPath}`;
};

export {
    GetStoredPasses,
    SavePassToStorage,
    ValidateFanForm,
    MaskPhone,
    MaskEmail,
    formatMexicoDate,
    ExportParticipantsToExcel,
    GetTriviaQuestions,
    SubmitTrivia,
    CheckDuplicateParticipant,
    LogScreenshot,
    GetExportDataset,
    TrackMetric,
    GetPublicConfig,
    RecoverPass,
    GetAdminConfig,
    UpdateAdminConfig,
    GetAdminQuestions,
    SaveAdminQuestion,
    DeleteAdminQuestion,
    GetAdminParticipants,
    GetAdminStats,
    DisqualifyParticipant,
    UndisqualifyParticipant,
    ResetDynamic,
    GetWinnerCount,
    CheckinParticipant,
    GetAdminOperatorName,
    PlayCheckinSound,
    SetOverlayVisibilityDL,
    SetOverlayOffsetDL,
    SetOverlayVisibility,
    SetOverlayOffset,
    UploadMedia,
    resolveMediaUrl,
    STORAGE_KEY
};



